import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Loader2 } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2ad418ae-721e-40fd-824f-e8019fae87b8/artifacts/20myis78_logotipo%20empresa.jpg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      setSubscribed(true);
      setEmail("");
      toast.success("Subscrição confirmada! Obrigado por se juntar a nós.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 400 && detail) {
        toast.info(detail);
      } else {
        toast.error("Não foi possível subscrever. Por favor tente novamente.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#3A4A38] text-white">
      <div className="container-custom py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img
              src={LOGO_URL}
              alt="Quinta da Oliveirinha"
              className="h-20 w-auto mb-6"
            />
            <p className="text-white/80 text-sm leading-relaxed">
              Do campo para a sua mesa. Produtos frescos, locais e sustentáveis,
              cultivados com amor e respeito pela natureza.
            </p>
          </div>

          {/* Navigation Column */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Navegação</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  data-testid="footer-link-home"
                  className="text-white/80 hover:text-white transition-colors text-sm"
                >
                  Início
                </Link>
              </li>
              <li>
                <Link
                  to="/sobre"
                  data-testid="footer-link-about"
                  className="text-white/80 hover:text-white transition-colors text-sm"
                >
                  Sobre Nós
                </Link>
              </li>
              <li>
                <Link
                  to="/produtos"
                  data-testid="footer-link-products"
                  className="text-white/80 hover:text-white transition-colors text-sm"
                >
                  Os Nossos Cabazes
                </Link>
              </li>
              <li>
                <Link
                  to="/como-funciona"
                  data-testid="footer-link-how"
                  className="text-white/80 hover:text-white transition-colors text-sm"
                >
                  Como Funciona
                </Link>
              </li>
              <li>
                <Link
                  to="/contacto"
                  data-testid="footer-link-contact"
                  className="text-white/80 hover:text-white transition-colors text-sm"
                >
                  Contacto
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Contactos</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-[#D4C4A8] mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:QuintadaOliveirinha23@gmail.com"
                  data-testid="footer-email"
                  className="text-white/80 hover:text-white transition-colors text-sm break-all"
                >
                  QuintadaOliveirinha23@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-[#D4C4A8] mt-0.5 flex-shrink-0" />
                <a
                  href="tel:+351938173638"
                  data-testid="footer-phone"
                  className="text-white/80 hover:text-white transition-colors text-sm"
                >
                  +351 938 173 638
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#D4C4A8] mt-0.5 flex-shrink-0" />
                <span className="text-white/80 text-sm">
                  Gomide, Vila Verde, Braga, Portugal
                </span>
              </li>
            </ul>
          </div>

          {/* Social & Newsletter Column */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Siga-nos</h4>
            <a
              href="https://www.instagram.com/quinta_da_oliveirinha/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm mb-6"
            >
              <Instagram size={20} />
              @quinta_da_oliveirinha
            </a>

            <div className="mt-6 pt-6 border-t border-white/20">
              <h5 className="text-sm font-semibold mb-3 text-white">Newsletter</h5>
              <p className="text-white/70 text-xs mb-3">
                Receba novidades sazonais e dicas da quinta.
              </p>
              {subscribed ? (
                <p
                  data-testid="footer-newsletter-success"
                  className="text-[#D4C4A8] text-sm"
                >
                  Obrigado! Está subscrito.
                </p>
              ) : (
                <form
                  onSubmit={handleNewsletterSubmit}
                  data-testid="footer-newsletter-form"
                  className="flex flex-col gap-2"
                >
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="o seu email"
                    data-testid="footer-newsletter-input"
                    aria-label="Subscrever newsletter"
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C4A8] focus:border-transparent"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="footer-newsletter-submit"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[#D4C4A8] text-[#3A4A38] font-medium text-sm hover:bg-[#E8DEC8] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting && <Loader2 size={14} className="animate-spin" />}
                    {submitting ? "A subscrever..." : "Subscrever"}
                  </button>
                </form>
              )}
              <p className="text-white/60 text-xs mt-4">
                Zona de entrega: Região de Braga e Vila Verde
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/60 text-sm">
              © {currentYear} Quinta da Oliveirinha. Todos os direitos reservados.
            </p>
            <p className="text-white/60 text-sm">
              Produção agrícola local – Portugal 🇵🇹
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
